import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import Swal from "sweetalert2";

import { validationSchema } from "./helpers/productForm.schema";
import { buildFormData } from "./helpers/productForm.utils";

import ProductBasicFields from "./form/ProductBasicFields";
import ProductImageField from "./form/ProductImageField";
import ProductCategoryFields from "./form/ProductCategoryFields";
import ProductVariationsSection from "./form/ProductVariationsSection";
import ProductDealsSection from "./form/ProductDealsSection";

import {
  AddProduct,
  UpdateProduct,
} from "../../../utils/product";

import {
  UpdateProductOption,
  AddProductOption,
  DeleteProductOption,
} from "../../../utils/productOptions";

export default function ProductFormDialog({
  open,
  onClose,
  initialValues,
  categories,
  onSaved,
}) {
  const handleSubmit = async (
    values,
    {
      resetForm,
      setSubmitting,
    }
  ) => {
    try {
      /*
       * buildFormData חייב להוסיף גם:
       * isInStock
       */
      const fd = buildFormData(values);

      if (values.id !== 0) {
        const savedProduct =
          await UpdateProduct(
            values.id,
            fd
          );

        onSaved?.({
          mode: "edit",
          product: savedProduct,
        });

        /*
         * עדכון הווריאציות הקיימות
         * והוספת וריאציות חדשות
         */
        if (
          values.variationType &&
          Array.isArray(values.options)
        ) {
          for (const option of values.options) {
            const optionData = {
              productId: values.id,
              optionType: values.variationType,
              value: (
                option.value || ""
              ).trim(),
              extraPrice:
                Number(option.extraPrice) || 0,
              quantity:
                Number(option.quantity) || 0,

              /*
               * חשוב להשתמש ב-??
               * כדי שהערך false לא יהפוך ל-true.
               */
              isInStock:
                option.isInStock ?? true,

              image:
                option.image instanceof File
                  ? option.image
                  : undefined,
            };

            if (
              option?.id &&
              option.id !== 0
            ) {
              await UpdateProductOption(
                option.id,
                optionData
              );
            } else if (
              (option.value || "").trim() !== ""
            ) {
              await AddProductOption({
                ...optionData,
                image:
                  option.image instanceof File
                    ? option.image
                    : null,
              });
            }
          }
        }

        resetForm();
        onClose();
        document.activeElement?.blur?.();

        setTimeout(() => {
          Swal.fire({
            icon: "success",
            title: "המוצר עודכן",
            timer: 1500,
            showConfirmButton: false,
          });
        }, 0);
      } else {
        const savedProduct =
          await AddProduct(fd);

        onSaved?.({
          mode: "add",
          product: savedProduct,
        });

        /*
         * הוספת וריאציות למוצר חדש
         */
        if (
          values.variationType &&
          Array.isArray(values.options)
        ) {
          const optionsToCreate =
            values.options.filter(
              (option) =>
                (
                  option.value || ""
                ).trim() !== ""
            );

          for (const option of optionsToCreate) {
            await AddProductOption({
              productId: savedProduct.id,
              optionType:
                values.variationType,
              value: (
                option.value || ""
              ).trim(),
              extraPrice:
                Number(option.extraPrice) ||
                0,
              quantity:
                Number(option.quantity) ||
                0,

              // מצב המלאי של הווריאציה
              isInStock:
                option.isInStock ?? true,

              image:
                option.image instanceof File
                  ? option.image
                  : null,
            });
          }
        }

        resetForm();
        onClose();
        document.activeElement?.blur?.();

        setTimeout(() => {
          Swal.fire({
            icon: "success",
            title: "המוצר נוסף",
            timer: 1500,
            showConfirmButton: false,
          });
        }, 0);
      }
    } catch (error) {
      console.error(
        "Failed to save product:",
        error
      );

      console.error(
        "Server response:",
        error?.response?.data
      );

      Swal.fire({
        icon: "error",
        title: "שמירה נכשלה",
        text:
          error?.response?.data?.message ||
          "אירעה שגיאה בשמירת המוצר",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      disableEnforceFocus
      disableRestoreFocus
      disableAutoFocus
    >
      <DialogTitle>
        {initialValues?.id
          ? "עריכת מוצר"
          : "הוסף מוצר"}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          dirty,
          isValid,
          isSubmitting,
          errors,
          touched,
          setFieldValue,
          values,
          getFieldProps,
        }) => (
          <Form encType="multipart/form-data">
            <DialogContent>
              <Grid container spacing={2}>
                <ProductBasicFields
                  errors={errors}
                  touched={touched}
                />

                <ProductImageField
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                <ProductCategoryFields
                  categories={categories}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* מצב המלאי של המוצר כולו */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      מצב המוצר באתר:
                    </Typography>

                    <Button
                      type="button"
                      variant="contained"
                      onClick={() =>
                        setFieldValue(
                          "isInStock",
                          !(
                            values.isInStock ??
                            true
                          )
                        )
                      }
                      color={
                        values.isInStock ??
                          true
                          ? "success"
                          : "error"
                      }
                      sx={{
                        minWidth: 0,
                        px: 1.5,
                      }}
                    >
                      {values.isInStock ??
                        true
                        ? "במלאי"
                        : "לא במלאי"}
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      כאשר המוצר מסומן כלא
                      במלאי, לא ניתן יהיה
                      להוסיף אף וריאציה שלו
                      לעגלה.
                    </Typography>
                  </Box>
                </Grid>

                <ProductVariationsSection
                  values={values}
                  setFieldValue={setFieldValue}
                  DeleteProductOption={
                    DeleteProductOption
                  }
                />

                <ProductDealsSection
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Grid>
            </DialogContent>

            <DialogActions>
              {getFieldProps("id").value !==
                0 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  שמור עריכת מוצר
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    !dirty ||
                    !isValid
                  }
                >
                  הוסף מוצר
                </Button>
              )}

              <Button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                ביטול
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}